/**
 * A legal document as a page of its own — `/terms-and-conditions`,
 * `/privacy-policy`, `/cookie-policy` and `/legal/<slug>`.
 *
 * It used to be a sheet laid over the landing page. A policy is something you
 * link to, print and open in a second tab from Settings or the register form,
 * so it is a route with the same header and footer as `/news`.
 *
 * The content never becomes HTML: `LegalPage.content` is an admin-editable
 * Markdown column, rendered by `markdown-lite.ts`, which builds nodes and
 * writes author text with `textContent`.
 */
import { BRAND } from './landing-copy';
import { loadLegalPage } from './landing-cms';
import { renderMarkdown } from './markdown-lite';
import { mountPage, type NewsViewOptions } from './news-view';

export function renderLegalPage(
  root: HTMLElement,
  slug: string,
  opts: NewsViewOptions,
): () => void {
  const { page, cleanups } = mountPage(root, opts, `
    <article class="lp-section lp-article lp-legal">
      <a class="lp-back" href="/">← Back to the settlement</a>
      <p class="lp-eyebrow" data-meta>Reading…</p>
      <h1 class="lp-h2" data-title></h1>
      <div class="lp-article-body" data-body tabindex="-1"></div>
    </article>`);

  const metaEl = page.querySelector<HTMLElement>('[data-meta]')!;
  const titleEl = page.querySelector<HTMLElement>('[data-title]')!;
  const bodyEl = page.querySelector<HTMLElement>('[data-body]')!;
  titleEl.textContent = titleFor(slug);
  let cancelled = false;
  cleanups.push(() => { cancelled = true; });

  document.title = `${titleFor(slug)} — ${BRAND.name}`;

  void loadLegalPage(slug)
    .then((doc) => {
      if (cancelled) return;
      titleEl.textContent = doc.title;
      metaEl.textContent = doc.updatedAt
        ? `Last updated ${new Date(doc.updatedAt).toLocaleDateString()}`
        : 'Current version';
      document.title = `${doc.title} — ${BRAND.name}`;
      renderMarkdown(bodyEl, doc.content);
    })
    .catch(() => {
      if (cancelled) return;
      metaEl.textContent = 'Unavailable';
      // The endpoint fails CLOSED and silently: an unseeded row, a page an
      // admin deactivated and a typo'd slug are the same 404.
      bodyEl.className = 'lp-article-body lp-sheet-error';
      bodyEl.textContent = `We could not load “${titleFor(slug)}” right now. `
        + 'It may not be published on this deployment yet. Please try again later.';
    });

  return () => {
    for (const fn of cleanups) fn();
    root.innerHTML = '';
  };
}

/** A readable heading before the fetch lands, and the only one if it never does. */
function titleFor(slug: string): string {
  return slug.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}
